import { useState } from 'react';

import { Message } from '../Message/Message.jsx';
import { Tooltip } from '../Tooltip/Tooltip.jsx';
import { ButtonSpinnerDarkIcon, ButtonSpinnerIcon } from '@svgs';

// FormPublishingFooter - save/draft/publish action bar; render as the last child in a <form>.
// Always required: publishMutation - the primary "commit" mutation (labeled "Save changes" in
// single-button mode, "Publish" in dual-button mode).
// Single "Save" mode (no draftMutation passed): just the one button, plus optional
// statusLabel/statusTooltip/statusUrl.
// Dual "Save draft"/"Publish" mode (draftMutation also passed, takes precedence): adds a second
// "Save draft" button bound to draftMutation, plus optional isDraft, draftUrl, publishedUrl,
// onRevert, showRevert (default true). Buttons are named "draft"/"publish" so onSubmit can read
// e.nativeEvent.submitter.name to pick which mutation to call.
// Common: disabled, isDirty, isValid, isError (combine multiple mutations' isError yourself).
// onRevert is called unguarded when Revert is confirmed - required whenever isDraft can be true
// and showRevert is true.
// No discard mutation is built in; the caller implements it.

export const FormPublishingFooter = ({
  disabled,
  draftMutation,
  draftUrl,
  isDirty,
  isError,
  isValid,
  isDraft,
  isReverted = false,
  onRevert,
  publishedUrl,
  publishMutation,
  showDraft = true,
  showRevert = true,
  statusLabel,
  statusTooltip,
  statusUrl,
}) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const hasPublishingActions = !!draftMutation;

  const handleRevert = () => {
    setShowConfirmation(false);
    onRevert();
  };

  return (
    <div className="sb-manage-content-button-bar">
      <div className="sb-flex">
        {showConfirmation && (
          <>
            <button
              type="button"
              className="sb-btn sb-btn-delete-confirm"
              onClick={handleRevert}
            >
              Yes, revert changes
            </button>
            <button
              type="button"
              className="sb-btn sb-btn-delete"
              onClick={() => setShowConfirmation(false)}
            >
              Cancel
            </button>
          </>
        )}
        {!showConfirmation && (
          <>
            {!hasPublishingActions && (
              <button
                type="submit"
                className="sb-btn sb-btn-save"
                disabled={disabled}
              >
                {publishMutation.isPending ? 'Saving...' : 'Save changes'}
                {publishMutation.isPending && <ButtonSpinnerIcon />}
              </button>
            )}
            {!hasPublishingActions && statusLabel && (
              <p className="sb-draft-status">
                <b>Status: </b>
                <Tooltip tooltip={statusTooltip}>
                  {statusUrl ? (
                    <a href={statusUrl} target="_blank">
                      {statusLabel}
                    </a>
                  ) : (
                    <span>{statusLabel}</span>
                  )}
                </Tooltip>
              </p>
            )}
            {hasPublishingActions && (
              <>
                {showDraft && (
                  <Tooltip tooltip="Save the current changes to draft. Viewable only on the preview or staging links">
                    <button
                      type="submit"
                      className="sb-btn sb-btn-cancel"
                      name="draft"
                      disabled={disabled}
                    >
                      {draftMutation.isPending ? 'Saving...' : 'Save draft'}
                      {draftMutation.isPending && <ButtonSpinnerDarkIcon />}
                    </button>
                  </Tooltip>
                )}
                <Tooltip tooltip="Publish the current changes live immediately">
                  <button
                    type="submit"
                    className="sb-btn sb-btn-save"
                    name="publish"
                    disabled={disabled}
                  >
                    {publishMutation.isPending ? 'Publishing...' : 'Publish'}
                    {publishMutation.isPending && <ButtonSpinnerIcon />}
                  </button>
                </Tooltip>
                {isDraft == false && (
                  <p className="sb-draft-status">
                    <b>Status: </b>
                    <Tooltip tooltip="Current content is published. Click to view live page">
                      <a href={publishedUrl} target="_blank">
                        Published
                      </a>
                    </Tooltip>
                  </p>
                )}
                {isDraft == true && (
                  <p className="sb-draft-status">
                    <b>Status: </b>
                    <Tooltip tooltip="Current content is in draft, click Publish to take these updates live">
                      <a href={draftUrl} target="_blank">
                        In draft
                      </a>
                    </Tooltip>
                    {showRevert && (
                      <>
                        {' '}
                        -{' '}
                        <Tooltip tooltip="Revert the changes in draft to the previously published version">
                          <button
                            type="button"
                            onClick={() => setShowConfirmation(true)}
                          >
                            Revert
                          </button>
                        </Tooltip>
                      </>
                    )}
                  </p>
                )}
              </>
            )}
          </>
        )}
      </div>
      {!isValid && isDirty && (
        <Message messageStyle="error" title="Please review your form" />
      )}
      {isError && (
        <Message
          messageStyle="error"
          title="Error saving changes"
          message="Please review your form"
        />
      )}
      {!hasPublishingActions && publishMutation?.isSuccess && (
        <Message messageStyle="success" title="Successfully saved" />
      )}
      {draftMutation?.isSuccess && (
        <Message
          messageStyle="success"
          title={
            isReverted ? 'Successfully reverted' : 'Successfully saved draft'
          }
        />
      )}
      {hasPublishingActions && publishMutation?.isSuccess && (
        <Message messageStyle="success" title="Successfully published" />
      )}
    </div>
  );
};
